import { useState } from 'react';

export enum StoreKeys {
  PREFERENCES_OVERRIDES_KEY = 'preferencesOverrides',
}

export const getLocal = <T>(key: string, initialValue: T) => {
  if (typeof window === 'undefined') {
    return initialValue;
  }
  try {
    // Get from local storage by key
    const item = window.localStorage.getItem(key);
    // Parse stored json or if none return initialValue
    return item ? (JSON.parse(item) as T) : initialValue;
  } catch (error) {
    // If error also return initialValue
    /* eslint-disable-next-line no-console -- Unexpected console statement. */
    console.log(error);
    return initialValue;
  }
};

export function useLocalStorage<T>(key: string, initialValue: T) {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    return getLocal(key, initialValue);
  });
  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch (error) {
      // A more advanced implementation would handle the error case
      // eslint-disable-next-line no-console -- Unexpected console statement.
      console.log(error);
    }
  };
  return [storedValue, setValue] as const;
}
